import { mapKeys, mapValues, kebabCase, bind, forIn } from 'lodash';

// Allows components to trigger global events using the global vue instance
// by adding a new method this.emitter.emit(...), which is equivalent to this.$root.$emit(...)
// To facilitate listening to these events, allows components to declare a new events object as part of its options
// Given a component like:
//    computed: { ... }
//    events: {
//      onScrollToView(payload){
//        // do something with payload
//      },
//      myEvent(payload) { ... }
//      ['making-my-life-hard'](payload) { ... }
//    },
//    methods: { ... }
// This mixin will automatically wire those functions to the events 'scroll-to-view', 'my-event' and 'making-my-life-hard'
// It converts automatically event names to kebab case (so the component declaration is nicer) and removes the 'on' prefix if found
// When the component is destroyed this mixin will automatically clean references
//
// Note: Bound handlers are stored on a component instance property (boundEvents) rather than on
// $options.events, because in Vue 3 $options is a shared cached object per component type.

const mixin = {
    beforeCreate() {
        const rawEvents = this.$options.events || {};

        // Allow events to be declared as "onScrollToView", and have them listening to the "scroll-to-view" event
        const kebabEvents = mapKeys(rawEvents, (method, eventName) => {
            if (eventName.startsWith('on')) eventName = eventName.slice(2);
            return kebabCase(eventName);
        });

        // Ensure all the event handlers receive the component instance as "this"
        this.boundEvents = mapValues(kebabEvents, method => bind(method, this));

        // Automatically wire all the events declared in the "events" section of the component
        // so they become listeners of events in the $root instance
        forIn(this.boundEvents, (method, eventName) => this.emitter.on(eventName, method));
    },
    beforeUnmount() {
        // Automatically cleanup of event listeners
        forIn(this.boundEvents, (method, eventName) => this.emitter.off(eventName, method));
    },
};

export default mixin;
